/**
 * Celebrations outside the table (D-203), after cardsclassic's ui/Celebrate.tsx, wallet/daily.css and table/motion.tsx:
 * - CelebrateBurst: rays and dots that pop once around an icon (success toasts, the badge on results, a friend's yes);
 * - CoinBurst: ten tokens flying up out of a Claim button;
 * - Confetti: lime, ivory and gold pieces over the screen for a win.
 * All of it is decoration: aria-hidden, no pointer events, gone with reduced motion (the player's switch, which the
 * shell writes to html[data-reduce-motion], or the phone's own setting), and every piece is over in about 1.5 s.
 */
import { useCallback, useEffect, useState, type CSSProperties, type ReactElement, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Icon } from "./Icon";
import "./celebrate.css";

/** Reduced motion, read where a hook can't be: the shell's attribute (prefs or system), or the system setting itself. */
export function reducedMotionNow(): boolean {
  if (typeof document === "undefined") return true;
  if (document.documentElement.dataset.reduceMotion === "true") return true;
  return typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/** The burst: eight rays and eight dots that pop once around an icon (or `children`). The icon stays when motion is reduced. */
export function CelebrateBurst({ icon, size = 22, children, className = "" }: { icon?: string; size?: number; children?: ReactNode; className?: string }): ReactElement {
  const still = reducedMotionNow();
  return (
    <span className={`celebrate-burst ${className}`} aria-hidden={children ? undefined : true} data-testid="celebrate-burst">
      {!still && (
        <svg className="celebrate-burst__rays" viewBox="0 0 48 48">
          {Array.from({ length: 8 }, (_, i) => <line key={i} x1="24" y1="8" x2="24" y2="3" transform={`rotate(${i * 45} 24 24)`} />)}
          {Array.from({ length: 8 }, (_, i) => <circle key={i} cx="24" cy="5" r="1.4" transform={`rotate(${i * 45 + 22.5} 24 24)`} />)}
        </svg>
      )}
      <span className="celebrate-burst__icon">{children ?? <Icon name={icon ?? "sparkles"} size={size} />}</span>
    </span>
  );
}

/**
 * Tokens out of a button, drawn over the page at a point on screen (the button usually goes once its token is taken,
 * and rows clip what overflows them). Use `useCoinBurst`. Nothing with reduced motion.
 */
export function CoinBurst({ x, y, count = 10 }: { x: number; y: number; count?: number }): ReactElement | null {
  if (reducedMotionNow() || typeof document === "undefined") return null;
  return createPortal(
    <span className="coin-burst" aria-hidden="true" data-testid="coin-burst" style={{ left: x, top: y }}>
      {Array.from({ length: count }, (_, i) => (
        <span key={i} className="coin-burst__coin" style={{ "--k": i, "--n": count } as CSSProperties}>
          <img src="/art/v5/glyphs/token.svg" width={20} height={20} alt="" draggable={false} />
        </span>
      ))}
    </span>,
    document.body,
  );
}

/** How long a coin burst stays mounted: the last coin starts at 315 ms and takes 900. */
export const COIN_BURST_MS = 1300;

/**
 * `fire(button)` bursts tokens out of the middle of `button` (call it once the claim succeeded); `node` goes anywhere in
 * the tree. Each fire restarts the burst.
 */
export function useCoinBurst(): { fire: (from: Element | null | undefined) => void; node: ReactNode } {
  const [at, setAt] = useState<{ key: number; x: number; y: number } | null>(null);
  useEffect(() => { if (!at) return; const t = setTimeout(() => setAt(null), COIN_BURST_MS); return () => clearTimeout(t); }, [at]);
  const fire = useCallback((from: Element | null | undefined) => {
    if (!from || reducedMotionNow()) return;
    const r = from.getBoundingClientRect();
    setAt((a) => ({ key: (a?.key ?? 0) + 1, x: r.left + r.width / 2, y: r.top + r.height / 2 }));
  }, []);
  return { fire, node: at ? <CoinBurst key={at.key} x={at.x} y={at.y} /> : null };
}

/** A repeatable 0–1 from a piece's index (no Math.random, so a re-render draws the same confetti). */
export const pieceRand = (i: number, k: number): number => { const x = Math.sin((i + 1) * 12.9898 * k + 4.1) * 43758.5453; return x - Math.floor(x); };

/** Confetti for a win: `count` pieces in three tones from the centre of the screen. Nothing with reduced motion. */
export function Confetti({ count = 32, className = "" }: { count?: number; className?: string }): ReactElement | null {
  if (reducedMotionNow()) return null;
  return (
    <div className={`confetti ${className}`} aria-hidden="true" data-testid="confetti">
      {Array.from({ length: count }, (_, i) => {
        const r = (k: number) => pieceRand(i, k);
        const style = {
          "--cx": `${(r(1) * 2 - 1) * 44}vw`, "--cy": `${-18 - r(2) * 30}vh`, "--cfall": `${28 + r(3) * 30}vh`,
          "--crot": `${(r(4) * 2 - 1) * 540}deg`, "--cdelay": `${Math.round(r(5) * 200)}ms`, "--cdur": `${Math.round(1100 + r(6) * 350)}ms`,
        } as CSSProperties;
        return <span key={i} className="confetti__bit" data-tone={i % 3} style={style} />;
      })}
    </div>
  );
}
